import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  useUnreadNotificationsCount,
  useMarkAllNotificationsRead,
  useMarkNotificationsBulkRead,
  useMarkNotificationRead,
  useInfiniteNotifications,
} from "@/hooks/useNotifications";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { NotificationsList } from "./NotificationsList";
import { useNotificationsSSE } from "./useNotificationsSSE";
import { BellIcon, CheckCheck, Check, Loader2 } from "lucide-react";
import React, { useMemo, useState, useCallback, useRef } from "react";
import { useSseNotificationToasts } from "./useSseNotificationToasts";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

type TabValue = "unread" | "all";

function getBadgeLabel(count?: number) {
  const c = count ?? 0;
  if (c > 99) return "99+";
  return String(c);
}

export const Notifications = (props: {
  hotelId?: string;
  enabled?: boolean;
}): React.JSX.Element => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<TabValue>("unread");
  const enabled = props.enabled ?? true;

  const scrollRef = useRef<HTMLDivElement>(null);

  const { newNotifications } = useNotificationsSSE({ enabled });
  useSseNotificationToasts({ notifications: newNotifications });

  const hotelId = props.hotelId ?? "";

  // We only fetch lists when popover is open
  const enabledForLists = Boolean(enabled && open);

  const { data: unreadCount } = useUnreadNotificationsCount({
    enabled,
    hotelId,
  });

  const unread = useInfiniteNotifications({
    enabled: enabledForLists,
    hotelId,
    isRead: false,
    limit: 4,
  });

  const all = useInfiniteNotifications({
    enabled: enabledForLists,
    hotelId,
    limit: 20,
  });

  const markAll = useMarkAllNotificationsRead();
  const markBulk = useMarkNotificationsBulkRead();
  const markSingle = useMarkNotificationRead();

  const badgeLabel = useMemo(
    () => getBadgeLabel(unreadCount?.count),
    [unreadCount?.count],
  );

  const currentItems = tab === "unread" ? unread.items : all.items;

  const currentUnreadIds = useMemo(() => {
    return currentItems.filter((n) => !n.isRead).map((n) => n.id);
  }, [currentItems]);

  const currentLoadingLabel = useMemo(() => {
    const loading =
      tab === "unread" ? unread.isInitialLoading : all.isInitialLoading;
    if (loading) return t("notifications.loading");
    return `${currentItems.length} ${currentItems?.length === 1 ? t("notifications.item") : t("notifications.items")}`;
  }, [
    tab,
    unread.isInitialLoading,
    all.isInitialLoading,
    currentItems.length,
    t,
  ]);

  const onMarkSingleRead = useCallback(
    (id: string) => {
      markSingle.mutate({ id, dto: { isRead: true } });
    },
    [markSingle],
  );

  const onMarkVisibleRead = useCallback(() => {
    if (currentUnreadIds.length === 0) return;
    markBulk.mutate({ notificationIds: currentUnreadIds, isRead: true });
  }, [currentUnreadIds, markBulk]);

  const unreadCountValue = unreadCount?.count ?? 0;
  const hasUnread = unreadCountValue > 0;
  const showBadge = hasUnread;

  const onTabChange = useCallback((v: string) => {
    setTab(v as TabValue);

    // Optional: scroll to top when switching tabs
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <BellIcon className="size-6" />

          {showBadge ? (
            <span
              className="
                absolute -right-1 -top-1
                min-w-[22px] h-[22px]
                px-1
                rounded-full
                bg-violet-500 text-white
                text-[12px] font-semibold
                flex items-center justify-center
                leading-none
                ring-2 ring-background
              "
              aria-label={t("notifications.unreadCountAria", {
                count: unreadCountValue,
              })}
            >
              {badgeLabel}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        className="w-[420px] p-0 overflow-hidden rounded-2xl"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="text-base font-semibold">
            {t("notifications.title")}
          </div>

          <Button
            variant="ghost"
            size="sm"
            className={hasUnread ? "gap-2" : "hidden"}
            onClick={() => markAll.mutate()}
            disabled={markAll.isPending || !hasUnread}
          >
            {markAll.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CheckCheck className="h-4 w-4" />
            )}
            {t("notifications.markAllRead")}
          </Button>
        </div>

        <Tabs value={tab} onValueChange={onTabChange} className="w-full">
          <div className="px-4 pt-3">
            <TabsList className="w-full grid grid-cols-2 rounded-[10px] bg-[#FBFAFF] p-[6px] h-[100%] shadow-sm">
              <TabsTrigger
                value="unread"
                className="
                  rounded-[8px] gap-2
                  border border-transparent
                  data-[state=active]:bg-[#F2F0FF]
                  data-[state=active]:border-[#C7BCFF]
                  data-[state=active]:shadow-xs cursor-pointer
                "
              >
                {t("notifications.unread")}
                {showBadge ? (
                  <span className="ml-1 rounded-full bg-white px-2 py-0.5 text-xs">
                    {badgeLabel}
                  </span>
                ) : null}
              </TabsTrigger>

              <TabsTrigger
                value="all"
                className="
                  rounded-[8px]
                  border border-transparent
                  data-[state=active]:bg-[#F2F0FF]
                  data-[state=active]:border-[#C7BCFF]
                  data-[state=active]:shadow-xs cursor-pointer
                "
              >
                {t("notifications.all")}
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="px-4 py-2 flex items-center justify-between">
            <div className="text-xs text-muted-foreground">
              {currentLoadingLabel}
            </div>

            <Button
              variant="outline"
              size="sm"
              className={hasUnread ? "gap-2" : "hidden"}
              onClick={onMarkVisibleRead}
              disabled={markBulk.isPending || currentUnreadIds.length === 0}
            >
              {markBulk.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              {t("notifications.markVisibleRead")}
            </Button>
          </div>

          <div
            ref={scrollRef}
            className="max-h-[520px] overflow-auto px-4 pb-4"
          >
            <TabsContent value="unread" className="m-0">
              <NotificationsList
                scrollContainerRef={scrollRef}
                isLoading={unread.isInitialLoading}
                isLoadingMore={unread.isLoadingMore}
                hasMore={unread.hasMore}
                items={unread.items}
                emptyText={t("notifications.noUnread")}
                loadingText={t("notifications.loading")}
                onMarkRead={onMarkSingleRead}
                onLoadMore={unread.loadMore}
              />
            </TabsContent>

            <TabsContent value="all" className="m-0">
              <NotificationsList
                scrollContainerRef={scrollRef}
                isLoading={all.isInitialLoading}
                isLoadingMore={all.isLoadingMore}
                hasMore={all.hasMore}
                items={all.items}
                emptyText={t("notifications.noNotifications")}
                loadingText={t("notifications.loading")}
                onMarkRead={onMarkSingleRead}
                onLoadMore={all.loadMore}
              />
            </TabsContent>
          </div>
        </Tabs>
      </PopoverContent>
    </Popover>
  );
};
